import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, Clock, User, Phone, Mail, MessageCircle, Copy, Check, ArrowRight, ArrowLeft, CreditCard, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TEST_IDS } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UPI_ID = "2233harshit-2@okicici";
const VERIFICATION_AMOUNT = 5;

const LOCATIONS = [
  { id: "andheri", name: "Andheri West", desc: "Western Mumbai · Epsom wellness experience" },
  { id: "santacruz", name: "Santacruz West", desc: "Flagship location · SV Road" },
  { id: "borivali", name: "Borivali West", desc: "North-west Mumbai · Gulmohar Road" },
];

const TIME_SLOTS = [
  "10:00 AM","11:00 AM","12:00 PM","1:00 PM","2:00 PM",
  "3:00 PM","4:00 PM","5:00 PM","6:00 PM","7:00 PM","8:00 PM","9:00 PM",
];

function buildGPayLink() {
  const params = new URLSearchParams({
    pa: UPI_ID,
    pn: "Epsom Wellness",
    am: String(VERIFICATION_AMOUNT),
    cu: "INR",
    tn: "Epsom Couple Reset Verification",
  });
  // Android intent specifically targeting Google Pay package
  const intentLink = `intent://pay?${params.toString()}#Intent;scheme=upi;package=com.google.android.apps.nbu.paisa.user;end`;
  // GPay-specific scheme as primary
  const gpayLink = `gpay://upi/pay?${params.toString()}`;
  // Tez scheme (older GPay India)
  const tezLink = `tez://upi/pay?${params.toString()}`;
  return { intentLink, gpayLink, tezLink };
}

export default function BookingSection() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    preferred_date: "", preferred_time: "", occasion: "", upi_transaction_id: "",
  });

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
  const today = new Date().toISOString().split("T")[0];

  const copyUPI = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopied(true);
      toast.success("UPI ID copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy. Please copy manually."); }
  };

  const openGPay = () => {
    const { intentLink, gpayLink, tezLink } = buildGPayLink();
    
    // Detect if Android or iOS
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    
    if (isAndroid) {
      // Use Android intent to specifically open Google Pay
      window.location.href = intentLink;
    } else if (isIOS) {
      // Use gpay:// scheme for iOS
      window.location.href = gpayLink;
    } else {
      // Desktop fallback — try gpay:// then tez://
      window.location.href = gpayLink;
    }
    
    // Fallback toast after delay
    setTimeout(() => {
      toast.info("If Google Pay didn't open, please pay ₹5 manually using the UPI ID below and enter your transaction reference.");
    }, 3000);
  };

  const validateStep1 = () => {
    if (!form.location) { toast.error("Please select your preferred Epsom location"); return false; }
    if (!form.name.trim()) { toast.error("Please enter your name"); return false; }
    const phone = form.phone.replace(/\D/g, "").slice(-10);
    if (phone.length !== 10 || !"6789".includes(phone[0])) { toast.error("Please enter a valid 10-digit Indian WhatsApp number"); return false; }
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("Please enter a valid email"); return false; }
    if (!form.preferred_date) { toast.error("Please select a date"); return false; }
    if (!form.preferred_time) { toast.error("Please select a time"); return false; }
    if (!consentChecked) { toast.error("Please agree to the terms to continue"); return false; }
    return true;
  };

  const handleNext = () => { if (validateStep1()) setStep(2); };

  const handleSubmit = async () => {
    if (!form.upi_transaction_id.trim()) { toast.error("Please enter your UPI Transaction / Reference ID"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, form);
      toast.success("Booking submitted successfully!");
      navigate(`/thank-you?ref=${encodeURIComponent(res.data.reference)}&name=${encodeURIComponent(form.name)}&location=${encodeURIComponent(form.location)}&date=${encodeURIComponent(form.preferred_date)}&time=${encodeURIComponent(form.preferred_time)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Booking failed. Please try again."); }
    setLoading(false);
  };

  return (
    <section id="booking" data-testid={TEST_IDS.bookingSection} className="py-16 sm:py-24 bg-[#0d0b09]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Section Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }} className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f2cf8b] mb-3">
            {step === 1 ? "Step 01 — Choose your location" : "Step 02 — Verify with ₹5"}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-[46px] font-semibold text-[#f8f0df] tracking-tight leading-tight">
            {step === 1 ? <>Your Epsom. <em className="italic text-[#bdb3a4]">Your time.</em></> : <>Pay ₹5 via <em className="italic text-[#f2cf8b]">GPay.</em></>}
          </h2>
          <p className="text-[#bdb3a4] text-sm sm:text-base mt-3 max-w-xl leading-relaxed">
            {step === 1
              ? "Select your preferred location first. We will collect your booking preferences and then take the ₹5 verification payment."
              : "Pay ₹5 refundable verification deposit via GPay to confirm your booking request."
            }
          </p>
        </motion.div>

        {/* Location Cards - Only in Step 1 */}
        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {LOCATIONS.map((loc) => (
              <button key={loc.id} data-testid={`${TEST_IDS.locationCard}-${loc.id}`} type="button" onClick={() => update("location", loc.name)}
                className={`relative text-left p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${
                  form.location === loc.name
                    ? "border-[#d7a85c] bg-[#14110e] transform -translate-y-0.5"
                    : "border-[rgba(242,207,139,0.18)] bg-[#14110e] hover:border-[#d7a85c]/50 hover:-translate-y-0.5"
                }`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-serif text-lg sm:text-xl font-semibold text-[#f8f0df] mb-1">{loc.name}</h3>
                    <p className="text-xs text-[#bdb3a4] leading-relaxed">{loc.desc}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border shrink-0 mt-1 ${
                    form.location === loc.name
                      ? "bg-[#d7a85c] border-[#d7a85c] shadow-[inset_0_0_0_5px_#17110e]"
                      : "border-[#665846]"
                  }`} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          {/* Form */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}
                  className="bg-gradient-to-b from-[#1b1510] to-[#120f0d] border border-[rgba(242,207,139,0.18)] rounded-2xl p-6 sm:p-7 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
                  <h3 className="font-serif text-2xl sm:text-[32px] font-semibold text-[#f8f0df] mb-2">Reserve your session</h3>
                  <p className="text-sm text-[#bdb3a4] mb-6 leading-relaxed">
                    Enter genuine booking details. Your ₹5 payment verifies the enquiry and helps our team prioritise your requested slot.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Full Name *</label>
                      <input data-testid={TEST_IDS.bookingNameInput} type="text" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" maxLength={80}
                        className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">WhatsApp Number *</label>
                      <input data-testid={TEST_IDS.bookingPhoneInput} type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 98XXXXXXXX" maxLength={15}
                        className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Email *</label>
                      <input data-testid={TEST_IDS.bookingEmailInput} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" maxLength={120}
                        className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Guests</label>
                      <select className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white focus:outline-none focus:border-[#d7a85c] text-sm">
                        <option value="2">2 — Couple</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Preferred Date *</label>
                      <input data-testid={TEST_IDS.bookingDateInput} type="date" min={today} value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)}
                        className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white focus:outline-none focus:border-[#d7a85c] text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Preferred Time *</label>
                      <select data-testid={TEST_IDS.bookingTimeSelect} value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)}
                        className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white focus:outline-none focus:border-[#d7a85c] text-sm appearance-none">
                        <option value="">Select time</option>
                        {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">Optional — Occasion / Note</label>
                    <textarea data-testid={TEST_IDS.bookingOccasionInput} value={form.occasion} onChange={(e) => update("occasion", e.target.value)}
                      placeholder="Date night, anniversary, birthday, just us, etc." rows={3} maxLength={300}
                      className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm resize-none" />
                  </div>

                  {/* Consent */}
                  <div className="mb-5">
                    <label className="flex gap-2.5 items-start text-xs text-[#aaa] leading-relaxed cursor-pointer">
                      <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
                        className="w-[18px] h-[18px] mt-0.5 shrink-0 accent-[#d7a85c]" />
                      <span>I agree to be contacted by Epsom regarding this booking request and understand that the ₹5 payment is a verification deposit subject to the published refund/adjustment terms.</span>
                    </label>
                  </div>

                  <button data-testid={TEST_IDS.bookingNextBtn} type="button" onClick={handleNext}
                    className="w-full inline-flex items-center justify-center gap-2 py-[17px] rounded-xl bg-gradient-to-br from-[#f1cb82] to-[#b77a31] text-[#1b1108] font-black text-[17px] shadow-[0_14px_35px_rgba(215,168,92,0.18)] hover:shadow-xl transition-all">
                    VERIFY BOOKING FOR ₹5 <ArrowRight className="w-5 h-5" />
                  </button>
                  <p className="text-xs text-[#9f968a] text-center mt-3">You will be redirected to GPay. Do not refresh while payment is processing.</p>
                </motion.div>
              ) : (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.4 }}
                  className="bg-gradient-to-b from-[#1b1510] to-[#120f0d] border border-[rgba(242,207,139,0.18)] rounded-2xl p-6 sm:p-7 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
                  <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-[#bdb3a4] hover:text-[#f2cf8b] transition-colors mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to details
                  </button>

                  <h3 className="font-serif text-2xl sm:text-[32px] font-semibold text-[#f8f0df] mb-2">Pay ₹5 Verification</h3>
                  <p className="text-sm text-[#bdb3a4] mb-6 leading-relaxed">
                    Tap the button below to pay <strong className="text-[#f8f0df]">₹5</strong> via GPay. This refundable deposit verifies your booking request.
                  </p>

                  {/* GPay Direct Button */}
                  <div className="flex flex-col items-center mb-8">
                    <button data-testid="gpay-pay-button" type="button" onClick={openGPay}
                      className="w-full max-w-sm inline-flex items-center justify-center gap-3 py-[17px] px-6 rounded-2xl bg-gradient-to-br from-[#f1cb82] to-[#b77a31] text-[#1b1108] font-black text-lg shadow-[0_14px_35px_rgba(215,168,92,0.25)] hover:shadow-xl transition-all duration-300">
                      <ExternalLink className="w-5 h-5" />
                      PAY ₹5 VIA GPAY
                    </button>
                    <p className="text-xs text-[#9f968a] mt-2 text-center">Opens GPay, PhonePe, Paytm or any UPI app</p>

                    {/* Fallback UPI ID */}
                    <div className="mt-5 w-full max-w-sm">
                      <p className="text-xs text-[#bdb3a4] text-center mb-2">Or pay manually — UPI ID:</p>
                      <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#0e0c0a] border border-[#3a3026]">
                        <span className="text-sm font-mono text-[#f2cf8b]">{UPI_ID}</span>
                        <button data-testid={TEST_IDS.copyUpiBtn} type="button" onClick={copyUPI} className="p-1.5 rounded-lg hover:bg-[#211a14] transition-colors">
                          {copied ? <Check className="w-4 h-4 text-[#39c47a]" /> : <Copy className="w-4 h-4 text-[#bdb3a4]" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Transaction ID */}
                  <div className="mb-6">
                    <label className="block text-xs uppercase tracking-[0.06em] font-extrabold text-[#d5c9b8] mb-2">UPI Transaction / Reference ID *</label>
                    <input data-testid={TEST_IDS.upiTransactionInput} type="text" value={form.upi_transaction_id} onChange={(e) => update("upi_transaction_id", e.target.value)}
                      placeholder="Enter your UPI transaction reference ID"
                      className="w-full px-4 py-[13px] rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm" />
                    <p className="text-xs text-[#9f968a] mt-1.5">Find this in your GPay/UPI app payment history.</p>
                  </div>

                  <button data-testid={TEST_IDS.bookingSubmitBtn} type="button" onClick={handleSubmit} disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 py-[17px] rounded-xl bg-[#39c47a] text-[#0d0b09] font-black text-[17px] shadow-lg shadow-[#39c47a]/20 hover:bg-[#2da563] transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : "CONFIRM BOOKING"}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 border border-[rgba(242,207,139,0.18)] rounded-2xl p-6 bg-[#15110e]">
              <h3 className="font-serif text-[27px] font-semibold text-[#f8f0df] mb-5">Your offer</h3>
              {[
                { l: "Experience", v: "Couple Reset" },
                { l: "Duration", v: "60 minutes" },
                { l: "Session price", v: "₹3,999" },
                { l: "GST", v: "5%" },
                { l: "Offer total", v: "₹4,198.95", bold: true },
                { l: "Today", v: "₹5", highlight: true },
              ].map((item, i) => (
                <div key={i} className="flex justify-between py-3 border-b border-[rgba(255,255,255,0.07)] last:border-b-0">
                  <span className="text-sm text-[#bdb3a4]">{item.l}</span>
                  <span className={
                    item.bold ? "font-black text-xl text-[#f8f0df]" :
                    item.highlight ? "font-black text-lg text-[#f2cf8b]" :
                    "font-semibold text-sm text-[#f8f0df]"
                  }>{item.v}</span>
                </div>
              ))}

              {/* ₹5 deposit info */}
              <div className="mt-5 p-4 rounded-xl border border-[rgba(57,196,122,0.25)] bg-[rgba(57,196,122,0.06)]">
                <p className="text-[13px] text-[#cfe9db] leading-relaxed">
                  <strong>✓ ₹5 refundable verification</strong><br/>
                  Your ₹5 payment is only for verification of the booking request, subject to the refund/adjustment policy shown at checkout.
                </p>
              </div>

              {/* Booking details */}
              {(form.location || form.preferred_date || form.preferred_time) && (
                <div className="mt-4 p-4 rounded-xl bg-[#0e0c0a] space-y-2">
                  {form.location && <div className="flex items-center gap-2 text-sm text-[#ded4c4]"><MapPin className="w-3.5 h-3.5 text-[#d7a85c]" />{form.location}</div>}
                  {form.preferred_date && <div className="flex items-center gap-2 text-sm text-[#ded4c4]"><Calendar className="w-3.5 h-3.5 text-[#d7a85c]" />{form.preferred_date}</div>}
                  {form.preferred_time && <div className="flex items-center gap-2 text-sm text-[#ded4c4]"><Clock className="w-3.5 h-3.5 text-[#d7a85c]" />{form.preferred_time}</div>}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {["Secure Payment", "WhatsApp Confirmation", "Location Selection"].map((t) => (
                  <span key={t} className="px-2.5 py-[7px] rounded-full text-[11px] font-medium text-[#c8beaf] border border-[rgba(242,207,139,0.18)]">{t}</span>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-[#a89d90] leading-relaxed">
                Final appointment confirmation depends on actual slot availability at the selected location. Please do not claim a confirmed appointment until Epsom confirms it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
